import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { shiftsApi } from './api';
import type {
  ApproveLateArrivalInput,
  AttendanceListParams,
  AttendanceListResult,
  AttendanceStats,
  CreateShiftInput,
  ShiftListParams,
  ShiftListResult,
  ShiftStats,
  ShiftStatsParams,
  ScheduleStaffInput,
  UpdateAttendanceInput,
  UpdateShiftInput,
  CreateSwapInput,
  SwapListParams,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useShifts(params: ShiftListParams): UseQueryResult<ShiftListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['shifts', tenantId, 'list', params.page ?? 1, params.location_id ?? '', params.is_active ?? '', params.search ?? ''],
    queryFn: () => shiftsApi.shifts(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useShiftStats(params: ShiftStatsParams): UseQueryResult<ShiftStats> {
  const tenantId = useTenantId();
  return useQuery({
    // Note: no is_active in the key — KPI totals are independent of the status
    // filter so they describe the whole dataset, not the filtered page (#170).
    queryKey: ['shifts', tenantId, 'stats', params.location_id ?? '', params.search ?? ''],
    queryFn: () => shiftsApi.shiftStats(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateShift() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShiftInput) => shiftsApi.createShift(input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts', tenantId] }); },
  });
}

export function useUpdateShift() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateShiftInput }) => shiftsApi.updateShift(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts', tenantId] }); },
  });
}

export function useDeactivateShift() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shiftsApi.deactivateShift(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts', tenantId] }); },
  });
}

export function useAttendance(params: AttendanceListParams): UseQueryResult<AttendanceListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['attendance', tenantId, 'list', params.page ?? 1, params.location_id ?? '', params.user_id ?? '', params.shift_id ?? '', params.start_date ?? '', params.end_date ?? '', params.status ?? ''],
    queryFn: () => shiftsApi.attendance(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useAttendanceStats(params: AttendanceListParams): UseQueryResult<AttendanceStats> {
  const tenantId = useTenantId();
  return useQuery({
    // No `page` in the key — the KPI totals describe the whole filtered range,
    // not the current page, so they stay constant as the user pages (#173).
    queryKey: ['attendance', tenantId, 'stats', params.location_id ?? '', params.user_id ?? '', params.shift_id ?? '', params.start_date ?? '', params.end_date ?? '', params.status ?? ''],
    queryFn: () => shiftsApi.attendanceStats(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useUpdateAttendance() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAttendanceInput }) => shiftsApi.updateAttendance(id, input),
    // Invalidate both the list and the dataset-scoped stats so the KPI cards
    // refresh when a record is edited (e.g. a late arrival is resolved) (#173).
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attendance', tenantId] }); },
  });
}

export function useApproveLateArrival() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ApproveLateArrivalInput }) =>
      shiftsApi.approveLateArrival(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attendance', tenantId] }); },
  });
}

// Assign (schedule) a staff member to a shift on a date. Refreshes the
// attendance list/stats so the new SCHEDULED record shows immediately.
export function useScheduleStaff() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleStaffInput) => shiftsApi.scheduleStaff(input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attendance', tenantId] }); },
  });
}

// ── Shift swaps ────────────────────────────────────────────────────────────
export function useShiftSwaps(params: SwapListParams) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['shift-swaps', tenantId, params.status ?? '', params.user_id ?? '', params.page ?? 1],
    queryFn: () => shiftsApi.listSwaps(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

function useSwapMutation(fn: (arg: never) => Promise<unknown>) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn as (arg: unknown) => Promise<unknown>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-swaps', tenantId] });
      qc.invalidateQueries({ queryKey: ['attendance', tenantId] });
    },
  });
}

export function useCreateSwap() {
  return useSwapMutation((input: CreateSwapInput) => shiftsApi.createSwap(input));
}
export function useApproveSwap() {
  return useSwapMutation((id: string) => shiftsApi.approveSwap(id));
}
export function useRejectSwap() {
  return useSwapMutation((id: string) => shiftsApi.rejectSwap(id));
}
export function useCancelSwap() {
  return useSwapMutation((id: string) => shiftsApi.cancelSwap(id));
}
